import * as React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Paper, Typography, Button, CircularProgress, Link } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { useTranslation } from 'react-i18next';
import AccountBlock from './account-block';
import SendingLabel from './sending-label';
import { useConsentStyles } from './styles';

const MESSAGES = {
  expired: 'mcp.consent.expired',
  network: 'mcp.consent.loadError',
  notApproved: 'mcp.consent.profileNotApproved',
  unavailable: 'mcp.consent.accountUnavailable',
  decided: 'mcp.consent.alreadyDecided',
};

const SEVERITY = {
  expired: 'error',
  network: 'error',
  notApproved: 'warning',
  unavailable: 'warning',
  decided: 'info',
};

export function LoadingCard() {
  const { t } = useTranslation();
  const classes = useConsentStyles();

  return (
    <Paper elevation={3} className={classes.card}>
      <div role="status" aria-live="polite" className={classes.loading}>
        <CircularProgress size={32} aria-hidden="true" />
        <Typography variant="body1">{t('mcp.consent.loading')}</Typography>
      </div>
    </Paper>
  );
}

// Every state of the screen that is not the consent card. No client data is
// shown here: the 403/404/409 answers carry none. The blocked states (403)
// offer only Deny, with the account loaded at boot.
export default function ConsentStateCard({
  kind,
  account,
  brandName,
  sending,
  actionError,
  onRetry,
  onDeny,
  onSwitchAccount,
}) {
  const { t } = useTranslation();
  const classes = useConsentStyles();
  const blocked = kind === 'notApproved' || kind === 'unavailable';
  const isSending = !!sending;

  return (
    <Paper elevation={3} className={classes.card}>
      <Alert severity={SEVERITY[kind]} className={classes.literal}>
        {t(MESSAGES[kind])}
      </Alert>

      {blocked && (
        <>
          <AccountBlock
            name={account.name}
            email={account.email}
            onSwitchAccount={onSwitchAccount}
            disabled={isSending}
          />
          {actionError && (
            <Alert severity="error" className={classes.alert}>
              {t('mcp.consent.actionError')}
            </Alert>
          )}
          <div className={classes.actions}>
            <Button
              variant="outlined"
              className={`${classes.btn} ${classes.deny}`}
              disabled={isSending}
              onClick={onDeny}
            >
              {sending === 'deny' ? <SendingLabel /> : t('mcp.consent.deny')}
            </Button>
          </div>
        </>
      )}

      {kind === 'network' && (
        <div className={classes.actions}>
          <Button variant="contained" color="primary" className={classes.btn} onClick={onRetry}>
            {t('mcp.consent.tryAgain')}
          </Button>
        </div>
      )}

      {(kind === 'expired' || kind === 'network') && (
        <Link component={RouterLink} to="/app" className={`${classes.link} ${classes.exitLink}`}>
          {t('mcp.consent.goToBrand', { brand: brandName })}
        </Link>
      )}
      {kind === 'decided' && (
        <Link
          component={RouterLink}
          to="/app/connected-apps"
          className={`${classes.link} ${classes.exitLink}`}
        >
          {t('mcp.consent.seeConnectedApps')}
        </Link>
      )}
    </Paper>
  );
}
